import {styled} from '@linaria/react';
import React, {FC} from 'react';
import {Video, VideoSourceType} from '../../atoms/Video/Video';

export type BannerMediaSource =
  | {kind: 'image'; url: string; alt?: string}
  | {kind: 'video'; sources: VideoSourceType[]; poster?: string; alt?: string};

export interface BannerMediaData {
  /** Hex color used as a fallback / behind the media. */
  backgroundColor?: string;
  src?: BannerMediaSource;
}

interface BannerMediaProps {
  media?: BannerMediaData;
  className?: string;
  ['data-testid']?: string;
}

export const BannerMedia: FC<BannerMediaProps> = ({
  media,
  className,
  'data-testid': dataTestid,
}) => {
  if (!media) return null;

  const bg = media.backgroundColor;

  if (!media.src) {
    return (
      <Fill
        className={className}
        data-testid={dataTestid}
        style={bg ? {backgroundColor: bg} : undefined}
      />
    );
  }

  if (media.src.kind === 'image') {
    // Use a CSS background-image on a div instead of an <img> tag — matches
    // the existing BannerImage approach and stays within the project's image
    // lint rule.
    return (
      <ImageFill
        className={className}
        data-testid={dataTestid}
        role="img"
        aria-label={media.src.alt}
        style={{
          backgroundImage: `url(${media.src.url})`,
          backgroundColor: bg,
        }}
      />
    );
  }

  return (
    <MediaVideo
      className={className}
      data-testid={dataTestid}
      sources={media.src.sources}
      poster={media.src.poster}
      autoPlay
      muted
      loop
      playsInline
      aria-label={media.src.alt}
      style={bg ? {backgroundColor: bg} : undefined}
    />
  );
};

const Fill = styled.div`
  width: 100%;
  height: 100%;
`;

const ImageFill = styled.div`
  width: 100%;
  height: 100%;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
`;

const MediaVideo = styled(Video)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;
